import {
  CAPTURE_FULL,
  isBuildingTile,
  isTransport,
  tileDefensePercent,
  tileName,
  unitDef,
  unitTotalHp,
} from "@dailywars/core/engine";
import type { Coord, GameState, Unit } from "@dailywars/core/engine";
import { Crosshair, Route } from "lucide-react";
import type { ReactNode } from "react";

import { UnitIconLayers } from "#/components/game/UnitIconLayers";
import { getRoadTileIconLayers } from "#/game/roadTileIcon";
import { getBuildingImageSrc, getTerrainImageSrc } from "#/game/svgAssets";
import {
  getSpriteTerrainImageSrc,
  usesSpriteTerrainFullTile,
  usesSpriteTerrainOverlay,
} from "#/game/terrainSprites";
import { cn } from "#/lib/utils";

interface TileInspectorBarProps {
  state: GameState;
  tile: Coord | null;
  unit: Unit | null;
  actions?: ReactNode;
}

const INSPECTOR_STAT_ICON_CLASS = "h-3 w-3 shrink-0";

function TerrainPanelIcon({ plainSrc, overlaySrc }: { plainSrc: string; overlaySrc?: string }) {
  if (!overlaySrc) {
    return <img src={plainSrc} alt="" className="size-full" aria-hidden />;
  }
  return (
    <>
      <img src={plainSrc} alt="" className="absolute inset-0 size-full" aria-hidden />
      <img src={overlaySrc} alt="" className="relative z-10 size-full" aria-hidden />
    </>
  );
}

function formatAttackRange({ minRange, maxRange }: { minRange: number; maxRange: number }): string {
  return minRange === maxRange ? String(minRange) : `${minRange}–${maxRange}`;
}

function UnitCombatRecord({ unit }: { unit: Unit }) {
  if (isTransport(unit.kind)) return null;
  const def = unitDef(unit.kind);
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
      <span>Kills: {unit.kills ?? 0}</span>
      {def.canCapture ? (
        <>
          <InspectorDivider className="h-3" />
          <span>Captures: {unit.captures ?? 0}</span>
        </>
      ) : null}
    </span>
  );
}

function UnitCargoLine({ cargo }: { cargo: NonNullable<Unit["cargo"]> }) {
  const name = unitDef(cargo.kind).name;
  const hp = unitTotalHp(cargo);
  return (
    <span className="text-xs text-muted-foreground tabular-nums">
      {name} (HP {hp}%)
    </span>
  );
}

function UnitPanel({ unit }: { unit: Unit }) {
  const def = unitDef(unit.kind);
  const hpNow = unitTotalHp(unit);
  const rangeLabel = formatAttackRange(def);
  return (
    <div className="flex w-full min-w-0 items-center gap-1 px-1 whitespace-nowrap">
      <UnitIconLayers
        kind={unit.kind}
        owner={unit.owner}
        className="-ml-3 -mr-1 h-12 w-12 shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1 leading-tight">
        <span className="truncate text-sm font-semibold text-foreground">{def.name}</span>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
          <span>HP {hpNow}%</span>
          <InspectorDivider className="h-3" />
          <span className="inline-flex items-center gap-1" aria-label={`Movement ${def.movement}`}>
            <Route className={INSPECTOR_STAT_ICON_CLASS} aria-hidden />
            {def.movement}
          </span>
          <InspectorDivider className="h-3" />
          <span
            className="inline-flex items-center gap-1"
            aria-label={`Attack range ${rangeLabel}`}
          >
            <Crosshair className={INSPECTOR_STAT_ICON_CLASS} aria-hidden />
            {rangeLabel}
          </span>
        </span>
        <UnitCombatRecord unit={unit} />
        {unit.cargo ? <UnitCargoLine cargo={unit.cargo} /> : null}
      </div>
    </div>
  );
}

function resolveTerrainPanelImageSources({
  tileObj,
  tile,
  theme,
}: {
  tileObj: NonNullable<GameState["tiles"][number]>;
  tile: Coord;
  theme: GameState["theme"];
}): { plainSrc: string; overlaySrc?: string } {
  const plainSrc = getTerrainImageSrc({
    kind: "plain",
    x: tile.x,
    y: tile.y,
    theme,
  });
  if (isBuildingTile(tileObj.kind)) {
    return {
      plainSrc,
      overlaySrc: getBuildingImageSrc(tileObj.kind, tileObj.owner ?? "neutral"),
    };
  }
  if (usesSpriteTerrainFullTile({ kind: tileObj.kind })) {
    return {
      plainSrc: getSpriteTerrainImageSrc({ theme, kind: tileObj.kind }),
    };
  }
  if (usesSpriteTerrainOverlay({ theme, kind: tileObj.kind })) {
    return { plainSrc, overlaySrc: getSpriteTerrainImageSrc({ theme, kind: tileObj.kind }) };
  }
  if (tileObj.kind === "road") {
    return getRoadTileIconLayers({ theme });
  }
  return {
    plainSrc: getTerrainImageSrc({ kind: tileObj.kind, x: tile.x, y: tile.y, theme }),
  };
}

function TerrainPanel({ state, tile }: { state: GameState; tile: Coord }) {
  const tileObj = state.tiles[tile.y * state.width + tile.x];
  if (!tileObj) return null;
  const name = tileName({ kind: tileObj.kind, theme: state.theme });
  const defensePercent = tileDefensePercent(tileObj);
  const capturing =
    tileObj.capturePoints !== undefined ? CAPTURE_FULL - tileObj.capturePoints : null;
  const isCapturing = capturing !== null && capturing > 0;
  const isCapturable = isBuildingTile(tileObj.kind) && tileObj.owner !== "p1";
  const { plainSrc, overlaySrc } = resolveTerrainPanelImageSources({
    tileObj,
    tile,
    theme: state.theme,
  });
  return (
    <div className="flex items-center gap-3 whitespace-nowrap ">
      <div className="flex flex-col gap-1 leading-tight text-right">
        <span className="text-sm font-semibold text-foreground">{name}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          Defense {defensePercent}%
        </span>
        {isCapturing ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            Capture {capturing}/{CAPTURE_FULL}
          </span>
        ) : isCapturable ? (
          <span className="text-xs text-muted-foreground">Capturable</span>
        ) : null}
      </div>
      <div className="relative h-10 w-10 shrink-0 overflow-hidden">
        <TerrainPanelIcon plainSrc={plainSrc} overlaySrc={overlaySrc} />
      </div>
    </div>
  );
}

function InspectorDivider({ className }: { className?: string }) {
  return <div className={cn("w-px shrink-0 bg-border", className)} aria-hidden />;
}

export function TileInspectorBar({ state, tile, unit, actions }: TileInspectorBarProps) {
  const unitSection = unit ? (
    <UnitPanel unit={unit} />
  ) : (
    <span className="text-xs font-normal text-muted-foreground">No unit</span>
  );

  const tileSection = tile ? (
    <TerrainPanel state={state} tile={tile} />
  ) : (
    <span className="text-xs font-normal text-muted-foreground">No tile</span>
  );

  return (
    <div className="border-t border-border px-2 sm:px-3 text-sm font-semibold text-foreground">
      {actions ? (
        <div className="flex items-center justify-between gap-2 border-b border-border py-2 sm:hidden">
          {actions}
        </div>
      ) : null}
      <div className="flex min-h-16 items-center gap-1 sm:gap-3 py-1 sm:h-20 sm:py-2">
        <div className="flex min-w-min basis-1/2 shrink-0 grow items-center sm:min-w-0 sm:flex-1">
          {unitSection}
        </div>
        {actions ? (
          <>
            <InspectorDivider className="hidden h-16 sm:block" />
            <div className="hidden shrink-0 items-center gap-2 sm:flex">{actions}</div>
          </>
        ) : null}
        <InspectorDivider className="h-12 sm:h-16" />
        <div className="flex min-w-0 shrink grow-0 basis-1/2 items-center justify-end sm:flex-1">
          {tileSection}
        </div>
      </div>
    </div>
  );
}
